import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { CropHealthViewModel, SatelliteViewModel } from '@/app/proxy/proxies';
import { DateTime } from 'luxon';
import { SwiperContainer } from 'swiper/element';
import { DialogService } from 'primeng/dynamicdialog';
import { PlantationService } from '@/app/core/services/plantation.service';
import { Swiper } from 'swiper/types';
import { CropHealthMapCalendarSelectDateModalComponent } from './crop-health-map-calendar-select-date-modal/crop-health-map-calendar-select-date-modal.component';
import { CropHealthService } from '../../../../services/crop-health.service';
import { CropHealthMapType } from '../crop-health-map-footer/crop-health-map-footer.component';
import { LanguageInitializerService } from '@/app/core/services/language-initializer.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-crop-health-map-calendar',
  imports: [SharedModule],
  templateUrl: './crop-health-map-calendar.component.html',
  styleUrl: './crop-health-map-calendar.component.less',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CropHealthMapCalendarComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() cropHealthData: CropHealthViewModel;
  @Input() fullScreenMode = false;
  @Input() selectedMapType: CropHealthMapType;
  availableDates: DateTime<boolean>[] = [];
  selectedDay: SatelliteViewModel;
  selectedDayIndex: number;

  @Output() readonly selectDateEvent = new EventEmitter<DateTime<boolean>>();
  @Output() readonly loadMore = new EventEmitter<any>();
  @Output() readonly dateSelectionPlaceEvent = new EventEmitter<string>();

  slides: SatelliteViewModel[] = [];
  slidesPerView = 7;

  @ViewChild('swiper', { static: true }) swiperContainer!: ElementRef;
  @ViewChild('swiper') swiperContainer1!: SwiperContainer;
  @ViewChild('slidesContainerEl') slidesContainerEl!: ElementRef;
  swiper: Swiper;
  locale: string;

  constructor(
    private dialogService: DialogService,
    private translateService: TranslateService,
    public plantationService: PlantationService,
    private cropHealthService: CropHealthService,
    private languageInitializerService: LanguageInitializerService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes?.['selectedMapType'] || changes?.['cropHealthData']) && this.cropHealthData) {
      this.updateHealthData();
    }

    if (changes?.['fullScreenMode'] && !changes?.['fullScreenMode'].firstChange) {
      setTimeout(() => {
        this.changeSlidesPerView();
      }, 200);
    }
  }

  ngOnInit(): void {
    this.cropHealthService.availableDates$.subscribe(res => {
      this.availableDates = res ?? [];
    });
    this.cropHealthService.selectedDay$.subscribe(res => {
      this.selectedDay = res.day;
      setTimeout(() => {
        this.setSlide(this.selectedDay)
      }, 100);
    });
    this.locale = this.languageInitializerService.getLocaleFromISO1()
  }

  ngAfterViewInit(): void {
    this.changeSlidesPerView();
    this.swiper = this.swiperContainer.nativeElement?.swiper as Swiper;
    this.swiper.on('slideChange', () => { });
    this.swiper.on('transitionEnd', (e: Swiper) => {
      if (e.isEnd) {
        // Do something
      }
    });
    this.swiper.on('slideChangeTransitionEnd', () => { });
  }

  updateHealthData() {
    // Update the slides based on the selected map type and crop health data
    let mapType = this.selectedMapType?.value;

    // If the selected map type is RISK (3), use NDVI images (0) instead
    // When the RISK Map is selected, actually we use the NDVI images and add red spots on the images.
    if (this.selectedMapType?.key === 'risk') {
      mapType = 0;
    }

    // Filter the crop health data to get the slides for the selected map type
    this.slides = this.cropHealthData?.mapList.filter((e) => e.cropMapTypes === mapType);

    // Check if the currently selected day is valid or needs to be updated
    // if selectedDay is null it means the page is loaded for the first time
    // but if the selectedDay already has value we need to check if it is still valid based on the selectedMapType and the date.
    // If it is not, then we selected the first slide again
    if (!this.selectedDay) {
      this.selectedDayIndex = 0;
      this.changeDay(this.slides[0]);
      setTimeout(() => {
        this.swiper.slideTo(this.selectedDayIndex, 300);
      });
    } else {
      if (this.selectedDay.cropMapTypes === mapType) {
        // If the selected day is valid, ensure the swiper is updated to the correct slide
        setTimeout(() => {
          this.swiper.slideTo(this.selectedDayIndex, 300);
        });
        this.changeDay(this.selectedDay);
      } else {
        this.selectedDayIndex = this.slides.findIndex(e => e.imageDate.hasSame(this.selectedDay.imageDate, 'day'));
        this.changeDay(this.slides[this.selectedDayIndex]);
      }
    }
  }

  changeSlidesPerView() {
    const swiperEl = this.swiperContainer.nativeElement as any;

    if (swiperEl?.swiper && this.slidesContainerEl) {
      const slidesPerView = this.slidesContainerEl.nativeElement.clientWidth / (this.fullScreenMode ? 100 : 90);
      const num = parseInt(slidesPerView.toString());
      swiperEl.setAttribute('slides-per-view', num);

      // Manually trigger update
      swiperEl.swiper.params.slidesPerView = +swiperEl.getAttribute('slides-per-view');
      swiperEl.swiper.update();
    }
  }

  onSelectDay(index: number) {
    // Mixpanel: date is selected by clicking a date tile/title in the swiper.
    this.dateSelectionPlaceEvent.emit('Date Tile');
    this.selectedDayIndex = index;
    this.changeDay(this.slides[this.selectedDayIndex]);

    if (index > this.slides.length - 3) {
      this.swiper.slideTo(this.slides.length - 1, 1000);
    }
  }

  nextDay() {
    // Mixpanel: date is selected via arrow navigation.
    this.dateSelectionPlaceEvent.emit('Arrow');
    if (this.selectedDayIndex === this.slides.length - 1) {
      this.swiper.slideTo(this.swiper.slides.length - 1, 600);
    } else {
      this.swiper.slideNext(600);
      this.selectedDayIndex += 1;
      this.changeDay(this.slides[this.selectedDayIndex]);
    }
  }

  previousDay() {
    // Mixpanel: date is selected via arrow navigation.
    this.dateSelectionPlaceEvent.emit('Arrow');
    if (this.selectedDayIndex > 0) {
      this.swiper.slidePrev(600);
      this.selectedDayIndex -= 1;
      this.changeDay(this.slides[this.selectedDayIndex]);
    }
  }

  changeDay(selectedDay: SatelliteViewModel) {
    this.selectedDay = selectedDay;
    this.cropHealthService.setSelectedDay({
      day: selectedDay,
      problematicSpots: this.cropHealthData?.problematicSpotView?.filter((e) =>
        e.date.hasSame(selectedDay.imageDate, 'day')
      ),
    })
  }

  openCalendarModal() {
    const modalRef = this.dialogService.open(CropHealthMapCalendarSelectDateModalComponent, {
      modal: true,
      header: this.translateService.instant('doktar_app.common.select_date'),
      closable: true,
      width: '330px',
      inputValues: {
        availableDates: this.availableDates,
        cropHealthData: this.cropHealthData,
      },
    });

    modalRef.onClose.subscribe((result: any) => {
      if (result) {
        // Mixpanel: date is selected via date picker modal.
        this.dateSelectionPlaceEvent.emit('Date Picker');
        const selectedDate = DateTime.fromJSDate(result);
        const day = this.cropHealthData.mapList.find(e => e.imageDate.hasSame(selectedDate, 'day') && e.cropMapTypes === this.selectedMapType.value)
        if (day) {
          this.setSlide(day);
          this.changeDay(day);
        } else {
          this.selectDateEvent.emit(DateTime.fromJSDate(result));
        }
      }
    });
  }

  private setSlide(day: SatelliteViewModel) {
    this.selectedDayIndex = this.slides.findIndex((e) => e.imageDate.hasSame(day.imageDate, 'day'));
    this.swiper.slideTo(this.selectedDayIndex, 500);
  }

  isThereProblematicSpot(date: DateTime<boolean>): boolean {
    return this.cropHealthData?.problematicSpotView?.some((e) => e.date.hasSame(date, 'day'));
  }

  get isThereMoreToLoad() {
    if (this.availableDates?.length > 0 && this.slides?.length > 0) {
      const lastAvailbaleImageDate = this.availableDates[this.availableDates.length - 1].toISODate();
      const lastLoadedImageDate = this.slides[this.slides.length - 1].imageDate.toISODate();
      return lastAvailbaleImageDate < lastLoadedImageDate;
    }
    return false;
  }
}
